import { isAxiosError } from 'axios';
import { WalletContractError } from '../../services/Wallet.service';
import type { tTopUpOptionsFailureKind } from './TopUpModal.types';

/**
 * 4xx que no son un veredicto de la wallet sobre este workspace.
 *
 * - 408 y 429: el servidor pidió esperar.
 * - 424: es el código con el que el BFF dice "wallet-service no respondió o
 *   respondió 5xx" (`CommunicationError`). Es un 4xx solo de nombre.
 * - 401 y 403: son de la sesión o de los permisos del usuario, no de la
 *   wallet. El BFF nunca reenvía un 401/403 de wallet-service (los colapsa a
 *   424), y `getTopUpOptions` usa `skipAutoRedirect`, así que una sesión
 *   vencida llega aquí como 401 en vez de mandar a login. El texto definitivo
 *   habla de los datos de pago, y para estos dos sería falso.
 */
const RETRYABLE_CLIENT_STATUSES: ReadonlySet<number> = new Set([
  401, 403, 408, 424, 429,
]);

/**
 * Clasifica una falla de `getTopUpOptions` (RET-2280).
 *
 * El BFF reenvía con su status los 4xx de wallet-service (`relayOrCollapse`
 * en bff-web): el 409 de un cliente de Stripe ambiguo y el 404 de un workspace
 * sin cliente llegan tal cual. Esas fallas no cambian al reintentar, así que
 * son `terminal`. Los 5xx, timeouts y caídas de wallet-service llegan como 424
 * o sin respuesta: `transient`.
 *
 * No reutiliza `classifySettlementFailure` (`Wallet.settlement.ts`): su lista
 * de definitivas (400, 401, 403, 422) es la de la lectura del saldo, y ahí el
 * 409 y el 404 serían pasajeros.
 */
export function classifyTopUpOptionsFailure(
  error: unknown,
): tTopUpOptionsFailureKind {
  if (error instanceof WalletContractError) return 'terminal';

  if (isAxiosError(error) && error.response) {
    const { status } = error.response;
    const isClientError = status >= 400 && status < 500;

    return isClientError && !RETRYABLE_CLIENT_STATUSES.has(status)
      ? 'terminal'
      : 'transient';
  }

  return 'transient';
}
